import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Observable, of, Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';

import { EvGateway, InstallationItemGatewayDevice } from '@vi-iot/client';

import { InstallationsMigrationStore } from '@store';
import { SearchType } from '@store';

import { InstallationMigrationModalComponent } from '../installation-migration-modal/installation-migration-modal.component';

@Component({
  selector: 'app-installation-migration-customer-devices',
  templateUrl: './installation-migration-customer-devices-modal.component.html',
  styleUrls: ['./installation-migration-customer-devices-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstallationMigrationCustomerDevicesModalComponent implements OnInit, OnDestroy {
  @Output() nextStep = new EventEmitter();
  @Output() previousStep = new EventEmitter();
  readonly batchSize = 25;
  customerNumber$: Observable<string>;
  searchType$: Observable<SearchType>;
  nextBatch = false;
  isAllCheckboxChecked = false;
  checkedInstallations: string[] = [];

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public installationsMigrationStore: InstallationsMigrationStore,
    private dialogRef: MatDialogRef<InstallationMigrationModalComponent>,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.searchType$ = of(SearchType.installationsToMigrate);
    this.customerNumber$ = this.installationsMigrationStore.customerNumber$.pipe(
      takeUntil(this.ngUnsubscribe$)
    );

    this.loadCompanyCustomerInstallations(this.batchSize);
    this.loadCachedCheckedInstallations();
  }

  onNextBatchChange($event: any): void {
    this.nextBatch = $event;
  }

  getRowHeight(): number {
    return window.innerWidth > 767 ? 40 : 120;
  }

  onFetchNext(): void {
    this.installationsMigrationStore.installationListCursor$.pipe(first()).subscribe((cursor) => {
      if (cursor) {
        this.loadCompanyCustomerInstallations(this.batchSize, cursor);
      }
    });
  }

  onToggleAll(): void {
    this.checkedInstallations = [];
    this.isAllCheckboxChecked = !this.isAllCheckboxChecked;

    this.cdr.markForCheck();
  }

  onToggleSingle(installationId: number): void {
    const idAsString = String(installationId);

    const elementPresentInList = this.checkedInstallations.includes(idAsString);

    if (this.isAllCheckboxChecked) {
      // Because we can't just select all but one installations we uncheck them all
      this.uncheckAll();
      setTimeout(() => {
        this.checkInstallation(idAsString);

        this.cdr.markForCheck();
      });
    } else if (elementPresentInList) {
      this.uncheckInstallation(idAsString);
    } else {
      this.checkInstallation(idAsString);
    }

    this.cdr.markForCheck();
  }

  onBack(): void {
    this.installationsMigrationStore.clearCachedInstallations();
    this.previousStep.emit();
  }

  onClose(): void {
    this.installationsMigrationStore.clearCachedData();
    this.dialogRef.close();
  }

  isChecked(installationId: number): boolean {
    const idAsString = String(installationId);

    return this.checkedInstallations.includes(idAsString) || this.isAllCheckboxChecked;
  }

  canMigrateInstallations(): boolean {
    return this.checkedInstallations.length > 0 || this.isAllCheckboxChecked;
  }

  onMigrateInstallations(): void {
    this.installationsMigrationStore.setInstallationsToMigrate(this.checkedInstallations);
    this.nextStep.emit();
  }

  getInstallationGateway(gateways: EvGateway[]): string {
    if (!gateways || !gateways.length) {
      return '';
    }
    const firstGatewayWithId = gateways.find((gateway) => !!gateway.serial);

    return firstGatewayWithId ? firstGatewayWithId.serial : '';
  }

  getMainDeviceWithMarketingName(gateways: EvGateway[]): InstallationItemGatewayDevice {
    if (!gateways && !gateways.length) {
      return null;
    }

    let devices = [];
    gateways.forEach((gateway) => {
      devices = [...devices, ...gateway.devices];
    });

    const mainDevices = devices.filter(
      (device: InstallationItemGatewayDevice) => device.isMainDeviceOfGateway
    );

    return mainDevices.find((device: InstallationItemGatewayDevice) => device.marketingName);
  }

  private checkInstallation(idAsString: string): void {
    this.checkedInstallations.push(idAsString);
  }

  private uncheckInstallation(idAsString: string): void {
    this.checkedInstallations = this.checkedInstallations.filter((id) => id !== idAsString);
  }

  private loadCompanyCustomerInstallations(itemsToLoadCount: number, cursor?: string): void {
    this.customerNumber$.pipe(first()).subscribe((customerNumber) => {
      const params = {
        oldCompanyNumber: customerNumber,
        limit: itemsToLoadCount,
        cursor,
      };
      this.installationsMigrationStore.listCompanyCustomerInstallations(params);
    });
  }

  private loadCachedCheckedInstallations(): void {
    this.installationsMigrationStore.installationsToMigrate$
      .pipe(first())
      .subscribe((cachedInstallations) => {
        this.checkedInstallations = [...cachedInstallations];
      });
  }

  private uncheckAll(): void {
    this.isAllCheckboxChecked = false;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
